import React, {useEffect, useState, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './_users.scss';
import {ROLE_DISPLAY, ROLES} from 'common/constants';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import {FaRegEye} from 'react-icons/fa';
import {Pagination, Loader} from 'components';
import { routes } from 'common';
import { generalExtraActions } from 'reduxStore/actions';

const filterOptions = [
  {label: ROLE_DISPLAY[ROLES.PLAYER], value: ROLES.PLAYER},
  {label: ROLE_DISPLAY[ROLES.GROUND_ADMIN], value: ROLES.GROUND_ADMIN},
  {label: ROLE_DISPLAY[ROLES.ADMIN], value: ROLES.ADMIN},
];

const sortOptions = [
  {value: '-created', label: 'Date Added (Newest)'},
  {value: 'created', label: 'Date Added (Oldest)'},
  {value: '-modified', label: 'Date Modified (Newest)'},
  {value: 'modified', label: 'Date Modified (Oldest)'},
];

function Users() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { users, usersLoading, usersError, usersPagination } = useSelector((state) => state.general);
  
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset page to 1 when filter, search, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search, sortBy, itemsPerPage]);

  // Build API parameters
  const buildApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      page_size: itemsPerPage,
    };
    if (sortBy) {
      params.ordering = sortBy;
    }
    if (filter !== 'all') {
      params.role = filter;
    }
    if (search) {
      params.search = search;
    }
    return params;
  }, [currentPage, itemsPerPage, sortBy, filter, search]);

  const handleFilter = (status) => {
    setFilter(prev => (prev === status ? 'all' : status));
  };

  const handleSearch = (_search) => {
    setSearch(_search);
  };

  const handleSortChange = (_sortBy) => {
    setSortBy(_sortBy);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => {
    setItemsPerPage(num);
  };

  const handleViewUser = (userId) => {
    navigate(`${routes.USERS}/${userId}`);
  };

  const handleCreateUser = () => {
    navigate(routes.USER_CREATE);
  };

  useEffect(() => {
    const params = buildApiParams();
    dispatch(generalExtraActions.getUsers(params));
  }, [dispatch, buildApiParams]);

  const totalItems = usersPagination.count;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (usersError) {
    return (
      <div className="users-page custom-listing">
        <div className="error-message">
          <h3>Error loading users</h3>
          <p>{usersError?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={() => {
            const params = buildApiParams();
            dispatch(generalExtraActions.getUsers(params));
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="users-page custom-listing">
      {usersLoading && <Loader />}
      <div className="listing-header">
        <h2 className="listing-title">Users:</h2>
        <button className="listing-create-btn" onClick={handleCreateUser}>Add new user</button>
      </div>
      <div className="listing-filters">
        <div className="filter-group">
          <h5>Filters:</h5>
          <Filters filters={filterOptions} selectedFilter={filter} handleFilter={handleFilter}/>
        </div>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={handleSortChange}/>
          <SearchBar 
            value={search} 
            placeholder="Search users..."
            debounceDelay={500}
            onDebouncedChange={handleSearch}
          />
        </div>
      </div>
      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Full Name</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Role</th>
            <th>Status</th>
            <th>Date Added</th>
            <th>Date Modified</th>
            <th>View</th>
          </tr>
          </thead>
          <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan="9" style={{textAlign: 'center', color: '#888'}}>
                No users found.
              </td>
            </tr>
          ) : (
            users.map((user, idx) => (
              <tr key={user.id || idx}>
                <td>{user.id}</td>
                <td>{user.full_name || 'N/A'}</td>
                <td>{user.email || 'N/A'}</td>
                <td>{user.phone_number || 'N/A'}</td>
                <td><span className={`badge badge-type`}>{ROLE_DISPLAY[user.role] || 'Unknown'}</span></td>
                <td>
                  <span
                    className={`badge badge-status${user.is_active ? '' : ' inactive'}`}>
                    {user.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>{user.created ? new Date(user.created).toLocaleDateString() : 'N/A'}</td>
                <td>{user.modified ? new Date(user.modified).toLocaleDateString() : 'N/A'}</td>
                <td>
                  <button 
                    className="view-btn" 
                    onClick={() => handleViewUser(user.id)}
                    title="View user details"
                  >
                    <FaRegEye/>
                  </button>
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
}

export default Users;